import api from './api';
import { ApiResponse, Prompt, PaginatedResponse } from '../models';

export interface PromptCreate {
  name: string;
  description?: string;
  template: string;
  variables?: string[];
  is_active?: boolean;
  is_default?: boolean;
}

export const promptService = {
  async getList(includeInactive = false): Promise<Prompt[]> {
    const params = includeInactive ? { page_size: 1000 } : { is_active: true, page_size: 1000 };
    const response = await api.get<ApiResponse<PaginatedResponse<Prompt>>>('/prompts/', { params });
    // Handle paginated response
    if (response.data.data && 'results' in response.data.data) {
      return response.data.data.results;
    }
    // Fallback for non-paginated response
    return response.data.data as unknown as Prompt[];
  },

  async getById(id: number): Promise<Prompt> {
    const response = await api.get<ApiResponse<Prompt>>(`/prompts/${id}/`);
    return response.data.data;
  },

  async getDefault(): Promise<Prompt | null> {
    try {
      const response = await api.get<ApiResponse<Prompt>>('/prompts/default/');
      return response.data.data;
    } catch {
      return null;
    }
  },

  async create(data: PromptCreate): Promise<Prompt> {
    const response = await api.post<ApiResponse<Prompt>>('/prompts/', data);
    return response.data.data;
  },

  async update(id: number, data: Partial<PromptCreate>): Promise<Prompt> {
    const response = await api.patch<ApiResponse<Prompt>>(`/prompts/${id}/`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/prompts/${id}/`);
  },

  async setDefault(id: number): Promise<Prompt> {
    const response = await api.post<ApiResponse<Prompt>>(`/prompts/${id}/set_default/`);
    return response.data.data;
  },

  async preview(id: number, context: Record<string, string>): Promise<{
    original: string;
    rendered: string;
    context: Record<string, string>;
  }> {
    const response = await api.post<ApiResponse<{
      original: string;
      rendered: string;
      context: Record<string, string>;
    }>>(`/prompts/${id}/preview/`, { context });
    return response.data.data;
  },
};
